import { z } from "zod";
import { IsoDateTime, Seq } from "./common.js";
export const SourceKind = z.enum([
    "vehicle-positions",
    "trip-updates",
    "alerts",
    "gtfs-static",
]);
/**
 * `status` jugé sur un décodage protobuf réel, jamais sur un HTTP 200 ni une
 * taille de réponse (CDC §2.1) :
 *  - ok      : la source publie des entités
 *  - empty   : elle répond, protobuf valide, mais zéro entité
 *  - failing : erreurs réseau / HTTP
 *  - down    : panne confirmée sur plusieurs cycles consécutifs
 */
export const SourceStatus = z.enum(["ok", "empty", "failing", "down"]);
/**
 * D'où vient la panne, établi sur des faits mesurés — jamais une estimation :
 *  - `cityway` : le fournisseur amont. On a la preuve que notre côté fonctionne
 *                (fetch abouti, ou d'autres sources du même hôte répondent), et
 *                que c'est SON flux qui est vide / en erreur / injoignable.
 *  - `sentinelle` : notre serveur. L'ingestion ne tourne plus, ou le moteur est
 *                à l'arrêt, ou toutes les sources tombent d'un coup (réseau
 *                sortant / DNS de la machine).
 */
export const SourceFaultCulprit = z.enum(["cityway", "sentinelle"]);
/**
 * Motif précis, décidé à partir du dernier cycle réellement observé
 * (`last_failure_reason`, code HTTP, état des autres sources, battements de
 * cœur ingestion + moteur). Pas d'heuristique floue : chaque valeur correspond
 * à une observation.
 */
export const SourceFaultReason = z.enum([
    // côté Cityway
    "cityway_publishing_empty", // HTTP 200, protobuf valide, 0 entité — Cityway ne publie plus rien
    "cityway_unreachable", // timeout / connexion refusée / DNS sur CETTE source seulement
    "cityway_http_error", // HTTP 5xx renvoyé par Cityway
    "cityway_rejected", // HTTP 401 / 403 / 429 — requête refusée (quota, droits)
    "cityway_endpoint_gone", // HTTP 404 — l'URL du flux a disparu
    "cityway_malformed", // réponse non décodable (page d'erreur HTML, tronquée)
    // côté Sentinelle (notre serveur)
    "sentinelle_ingest_stalled", // l'ingestion n'a pas bouclé depuis trop longtemps
    "sentinelle_engine_stalled", // le moteur de courses n'a pas recalculé depuis trop longtemps
    "sentinelle_network", // plusieurs sources tombent simultanément — réseau sortant de la machine
]);
/**
 * Attribution de panne d'une source. `null` sur `SourceHealth.fault` = la
 * source va bien. Le champ `detail` est une phrase française prête à afficher
 * dans la fenêtre « d'où vient la panne ».
 */
export const SourceFault = z.object({
    culprit: SourceFaultCulprit,
    reason: SourceFaultReason,
    detail: z.string(),
    since: IsoDateTime.nullable(), // depuis quand cet état dure
    httpStatus: z.number().int().nullable(), // dernier code HTTP observé, si pertinent
    evidence: z.string(), // ce qui prouve l'attribution (ex. « TAE/TNI répondent depuis le même hôte »)
});
export const SourceHealth = z.object({
    sourceId: z.string(),
    operatorId: z.string(),
    kind: SourceKind,
    status: SourceStatus,
    lastDataAt: IsoDateTime.nullable(),
    lastEntityCount: z.number().int().nonnegative(),
    consecutiveEmptyCycles: z.number().int().nonnegative(),
    fallback: z.enum(["none", "scheduled"]),
    since: IsoDateTime.nullable(), // début de la bascule à l'horaire théorique ; null si fallback=none
    affectedLineIds: z.array(z.string()), // D-002 — lignes retombées à l'horaire théorique à cause de CETTE source
    fault: SourceFault.nullable(), // D-020 — attribution de panne ; null si la source va bien
});
/**
 * Battement de cœur d'un maillon interne (ingestion, moteur). Sert à distinguer
 * « Cityway ne publie plus » de « notre serveur ne tourne plus ».
 */
export const HeartbeatHealth = z.object({
    lastAt: IsoDateTime.nullable(),
    ageSeconds: z.number().int().nonnegative().nullable(),
    stalled: z.boolean(),
});
/**
 * D-020 — verdict global sur le temps réel, pour la pastille « temps réel » /
 * « temps réel dégradé » du frontend et sa fenêtre de détail.
 *  - `nominal`  : tout publie
 *  - `degraded` : au moins une source est retombée à l'horaire théorique, mais
 *                 notre serveur tourne
 *  - `down`     : notre serveur (ingestion ou moteur) est à l'arrêt — toutes
 *                 les positions affichées sont figées
 */
export const RealtimeVerdict = z.enum(["nominal", "degraded", "down"]);
export const RealtimeDiagnosis = z.object({
    verdict: RealtimeVerdict,
    headline: z.string(), // phrase française résumant l'état, prête à afficher
    culprit: SourceFaultCulprit.nullable(), // responsable dominant ; null si nominal
    ingest: HeartbeatHealth, // l'ingestion GTFS-RT boucle-t-elle encore ?
    engine: HeartbeatHealth, // le moteur de courses recalcule-t-il encore ?
    observedAt: IsoDateTime,
});
export const SourcesHealthResponse = z.object({
    seq: Seq, // D-009 — le sujet `sources` du flux remplace la liste entière à chaque delta
    sources: z.array(SourceHealth),
    realtime: RealtimeDiagnosis, // D-020 — verdict + battements de cœur ; absent du flux SSE `sources` (endpoint REST uniquement)
});
//# sourceMappingURL=sources.js.map